import {
  createContext,
  FC,
  useCallback,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import useAsyncEffect from 'use-async-effect';

import { useMytaverse } from '@providers/MytaverseProvider';
import { useMytaverseEvent } from '@core/dashboard/providers/MytaverseEventProvider';
import { getStorageValue, setStorageValue } from '@helpers/storage';
import { StorageTypes, StorageValues } from '@interfaces/storage';
import { WebsocketAction } from '@interfaces/webSocketConnectionInfo';
import EventsService from '@services/EventsService';
import ParticipantsService from '@services/ParticipantsService';

import {
  IMessage,
  MessageType,
  EAudioPlaybackType,
} from '@core/assistant/interfaces';
import {
  assistantSize,
  bedrockResultNotFoundReply,
  useAudioZoneBasedContext,
  debugEventId,
  debugTagName,
} from '@core/assistant/constants';
import { SCREENS, AWAITING_RESPONSE_TYPE } from '@core/assistant/interfaces';
import { celebratoryMomentAudioZone } from '@core/assistant/audioZoneScript';
import {
  defaultBedrockReply,
  teleportRegions,
  keys,
  EKeyName,
} from '@core/assistant/voiceCommand';

// TODO: remove socket.io
import {
  IAssistantContext,
  ProviderProps,
  SocketEventRequestType,
  SocketEventResponseType,
  SocketEventDirection,
} from './interfaces';
import { AudioVisualizer, visualizeAudio } from './AudioVisualizer';
import { socket } from './Socket';
import { useMicRecord } from './Record';
import { shared } from './Shared';
import { processActionsAudioPrompt } from './VoiceInput';
import { log } from './Debug';

import {
  LinearScriptPlayer,
  ScriptNode,
} from './scriptPlayer/linearScriptPlayer';
import { useUserName } from './UserName';
import { ScriptNodeJSON } from './scriptPlayer/intefraces';
import AssistantScriptsService from '@services/AssistantScriptService';
import { IAssistantScript } from '@interfaces/assistantScript';
import AssistantQueryService from '@services/AssistantQueryService';
import { regionNameToCamelCase, formatSpotRegionName } from './Regions';

export const AssistantContext = createContext<IAssistantContext>(
  {} as IAssistantContext,
);

export const useAssistant = () => useContext(AssistantContext);

let visualizerInstance: any;
// TODO: temp workaround, do a proper fix on SM side to have an option to use Transcibe only
// TODO: get rid of AWAITING_RESPONSE_TYPE.LONG
let activeScreenGlob = SCREENS.DEFAULT;
// TODO: temp workaround
let isMutedGlob = false;
let currentRegion = '';
let awaitingResponseTypeGlob = '';

const completedScripts: { [key: string]: boolean } = {};

const getCurrentRegion = () => {
  let localCurrentRegion = currentRegion;

  if (!currentRegion && debugTagName) {
    localCurrentRegion = debugTagName;
  } else if (!currentRegion && !debugTagName) {
    localCurrentRegion = 'landing';
  }

  return localCurrentRegion;
};

export const AssistantProvider: FC<ProviderProps> = ({ children }) => {
  const userName = useUserName();

  const [open, setOpen] = useState(false);
  const [isAudioPlaybackActive, setIsAudioPlaybackActive] = useState(false);
  const [awaitingResponseType, setAwaitingResponseType] = useState(
    AWAITING_RESPONSE_TYPE.NONE,
  );
  const [activeScreen, setActiveScreen] = useState<SCREENS>(SCREENS.DEFAULT);
  const [navigationHistory, setNavigationHistory] = useState<SCREENS[]>([
    SCREENS.DEFAULT,
  ]);
  //const [audioPlayBackType, setAudioPlayBackType] = useState<EAudioPlaybackType>();

  const [scripts, setScripts] = useState<IAssistantScript[]>([]);

  const [audioPromptHistory, setAudioPromptHistory] = useState<string[]>([]);

  const [isConnected, setIsConnected] = useState(socket.connected);
  const [isRecording, setIsRecording] = useState(false);
  const [isMuted, setIsMutedLoc] = useState(false);
  const [isDisplayUserFeedback, setIsDisplayUserFeedback] = useState(false);
  const [lastUserMessageData, setLastUserMessageData] = useState({
    eventId: '',
    participantId: '',
    timestamp: 0,
  });

  const [chatHistory, setChatHistory] = useState<IMessage[]>([]);

  const progressState = useRef('{}');

  const { sendJSONMessageToWebSocket } = useMytaverse();
  let mytaverseEventProvider = useMytaverseEvent();

  const { stopRecording } = useMicRecord();
  // const { isAudioZonePlayback, setIsAudioZonePlayback } = useAudioZone();
  // const { isGreetingsPlayback, setIsGreetingsPlayback } = useGreetings();

  const linearScriptPlayerRef = useRef<LinearScriptPlayer>();
  const isAudioPlaybackActiveRef = useRef<boolean>(false);

  const setIsMuted = () => {
    setIsMutedLoc(!isMuted);
    isMutedGlob = !isMutedGlob;
  };

  const navigate = (screen: SCREENS, navigateBack?: boolean) => {
    if (navigateBack) {
      activeScreenGlob = navigationHistory[navigationHistory.length - 2];
      setActiveScreen(navigationHistory[navigationHistory.length - 2]);
    } else {
      activeScreenGlob = screen;
      setActiveScreen(screen);
    }

    setNavigationHistory((prevState) => {
      if (navigateBack) {
        prevState.pop();
        return [...prevState];
      }

      return [...prevState, screen];
    });
  };

  const addMessage = useCallback(
    ({ text, type }: { text: string; type: MessageType }) => {
      setChatHistory((prevState) => {
        return [...prevState, { text, type, timestamp: Date.now() }];
      });
    },
    [],
  );

  const addAudioHistory = useCallback((text: string) => {
    setAudioPromptHistory((prevState) => {
      return [...prevState, text];
    });
  }, []);

  const handleSendRequest = useCallback(
    async (text: string) => {
      const localCurrentRegion = getCurrentRegion();
      addMessage({ text, type: MessageType.USER });
      setIsDisplayUserFeedback(false);
      setAwaitingResponseType(AWAITING_RESPONSE_TYPE.SHORT);

      shared.audioPlayBackType = EAudioPlaybackType.CHAT;
      console.warn('currentRegion', currentRegion);

      if (useAudioZoneBasedContext) {
        console.warn(
          'useAudioZoneBasedContext currentRegion',
          localCurrentRegion,
        );
        console.warn(mytaverseEventProvider?.currentEventId);

        const modelResponse = await AssistantQueryService.queryAssistant({
          text,
          tag: formatSpotRegionName(localCurrentRegion),
          eventId: mytaverseEventProvider?.currentEventId || 'debug',
          includeScriptContext:
            mytaverseEventProvider?.currentEventId || debugTagName
              ? true
              : false,
        });

        setAwaitingResponseType(AWAITING_RESPONSE_TYPE.NONE);
        awaitingResponseTypeGlob = AWAITING_RESPONSE_TYPE.NONE;

        if (modelResponse.text !== "I don't know.") {
          generatePollyAudio(modelResponse.text);
        }

        addMessage({ text: modelResponse.text, type: MessageType.ASSISTANT });

        if (
          modelResponse.text === "I don't know." &&
          modelResponse.lastUserMessageData
        ) {
          setIsDisplayUserFeedback(true);
          setLastUserMessageData(modelResponse.lastUserMessageData);
        }

        console.warn('useAudioZoneBasedContext modelResponse', modelResponse);
      } else {
        socket.emit(SocketEventDirection.request, {
          type: SocketEventRequestType.bedrock,
          data: { text },
        });
      }
    },
    [addMessage],
  );

  const handleSubmitUserFeedback = async (isUserApproved: boolean) => {
    await AssistantQueryService.flagAssistantReply({
      eventId: lastUserMessageData.eventId,
      timestamp: lastUserMessageData.timestamp,
      isFeedbackRequired: isUserApproved ? 2 : 3,
    });
    setIsDisplayUserFeedback(false);

    if (isUserApproved) {
      addMessage({
        text: 'Your request is recorded',
        type: MessageType.ASSISTANT,
      });
    }
  };

  function isAIEnabledForEvent() {
    if (
      mytaverseEventProvider &&
      mytaverseEventProvider.currentEventId &&
      mytaverseEventProvider.events.find(
        (x) => x.eventId === mytaverseEventProvider.currentEventId,
      )?.settings?.aiEnabled
    ) {
      return true;
    }

    return false;
  }

  function audioEndCallback() {
    isAudioPlaybackActiveRef.current = false;

    const linearScriptPlayerState =
      linearScriptPlayerRef.current?.getCurrentState();

    if (
      linearScriptPlayerState?.isRunning &&
      !linearScriptPlayerState?.currentNode.advanceOnAudioEnd
    ) {
      setIsAudioPlaybackActive(false);
    }

    console.log('audioEndCallback', {
      audioPlayBackType: shared.audioPlayBackType,
      currentRegion,
      linearScriptPlayerState,
    });

    if (
      linearScriptPlayerState?.isRunning &&
      linearScriptPlayerState?.currentNode.advanceOnAudioEnd
    ) {
      console.warn('auto advance script');
      linearScriptPlayerRef.current?.triggerNext();
      console.warn(linearScriptPlayerRef.current?.getCurrentState());

      /*if (
        !linearScriptPlayerRef.current?.getCurrentState().isRunning &&
        linearScriptPlayerRef.current?.getCurrentState().name
      ) {
        completedScripts[
          linearScriptPlayerRef.current?.getCurrentState().name
        ] = true;
      }*/

      console.warn('completedScripts', completedScripts);
    }

    console.warn(
      'current node',
      linearScriptPlayerRef.current?.getCurrentState().currentNode.id,
    );

    if (currentRegion === celebratoryMomentAudioZone) {
      log('trigger celebratory moment websocket message');

      const wsMessage = {
        action: WebsocketAction.MytaverseAction,
        tag: 'MytaEvent.Action.CelebrationMoment.Start',
        data: '',
      };
      log('sendJSONMessageToWebSocket', wsMessage);

      sendJSONMessageToWebSocket(wsMessage);
    }

    if (shared.audioPlayBackType === EAudioPlaybackType.AUDIO_ZONE) {
      //setOpen(false);
    }

    if (shared.audioPlayBackType === EAudioPlaybackType.GREETINGS) {
      const assistantStorage = getStorageValue<any>(
        StorageValues.Assistant,
      ) || {
        isFirstTime: true,
      };

      if (!assistantStorage.isFirstTime) {
        //setOpen(false);
      }
    }

    //setIsGreetingsPlayback(false);
    //setIsAudioZonePlayback(false);
  }

  function stopPlayback() {
    isAudioPlaybackActiveRef.current = false;
    setIsAudioPlaybackActive(false);

    try {
      visualizerInstance.stopAudio();
    } catch (e) {}
  }

  function teleportToRegion(regionName: string) {
    log('teleport region', regionName);

    const wsMessage = {
      action: WebsocketAction.MytaverseAction,
      tag: 'MytaEvent.Action.Teleport.ToRegion',
      data: regionName,
    };
    log('sendJSONMessageToWebSocket', wsMessage);

    sendJSONMessageToWebSocket(wsMessage);
  }

  function onConnect() {
    log('connected to AI API');
    setIsConnected(true);
  }

  function onDisconnect() {
    log('disconnected from AI API');
    setIsConnected(false);
  }

  async function onQueryBedrock(data: any) {
    if (
      activeScreenGlob !== SCREENS.DEFAULT &&
      activeScreenGlob !== SCREENS.TELEPORT
    ) {
      // if custom model cannot find answer retry query using general purpose model
      if (
        awaitingResponseTypeGlob !== AWAITING_RESPONSE_TYPE.LONG &&
        data.includes(bedrockResultNotFoundReply)
      ) {
        setAwaitingResponseType(AWAITING_RESPONSE_TYPE.LONG);
        awaitingResponseTypeGlob = AWAITING_RESPONSE_TYPE.LONG;
      } else {
        setAwaitingResponseType(AWAITING_RESPONSE_TYPE.NONE);
        awaitingResponseTypeGlob = AWAITING_RESPONSE_TYPE.NONE;
        generatePollyAudio(data);
        addMessage({ text: data, type: MessageType.ASSISTANT });
      }
    }
  }

  function onGenerateAudio(data: any) {
    log('activeScreen', activeScreenGlob);
    log('isMutedGlob', isMutedGlob);

    if (
      /*activeScreenGlob !== SCREENS.DEFAULT &&*/ !isMutedGlob &&
      !isAudioPlaybackActiveRef.current
    ) {
      log('onGenerateAudio data len', data.length);
      isAudioPlaybackActiveRef.current = true;
      console.warn('isAudioPlaybackActiveRef', isAudioPlaybackActiveRef);
      setIsAudioPlaybackActive(true);

      if (visualizerInstance) {
        visualizerInstance.stopAudio();
      }
      visualizerInstance = visualizeAudio(
        assistantSize.width,
        assistantSize.height,
        audioEndCallback,
      );
      visualizerInstance.initialize(data);
    }
  }

  function onTranscription(data: any) {
    log('onTranscription', data);

    if (data.isFinal) {
      stopRecording();
      setIsRecording(false);

      if (activeScreenGlob === SCREENS.DEFAULT) {
        const actionsTranscribePromptResult = processActionsAudioPrompt(
          data.text.toLowerCase(),
        );

        log('actions prompt', {
          prompt: data.text,
          result: actionsTranscribePromptResult,
        });

        if (actionsTranscribePromptResult) {
          shared.audioPlayBackType = EAudioPlaybackType.VOICE_COMMAND;

          if (actionsTranscribePromptResult.indexOf('expression') > -1) {
            const enterEvent = new KeyboardEvent(
              'keydown',
              keys[actionsTranscribePromptResult as EKeyName],
            );
            // TODO
            /*mytaverseEventProvider.gameCastRef.current.detachInput();
            mytaverseEventProvider.gameCastRef.current.attachInput();
            mytaverseEventProvider.gameCastRef.current.processKeyboardEvent(
              enterEvent,
            );*/
          } else if (actionsTranscribePromptResult.indexOf('teleport') > -1) {
            for (let i = 0; i < teleportRegions.length; i++) {
              if (
                actionsTranscribePromptResult.indexOf(teleportRegions[i].name) >
                -1
              ) {
                teleportToRegion(teleportRegions[i].ueName as string);
                break;
              }
            }
          }

          addAudioHistory(data.text);
        } else {
          // playback default audio reply
          generatePollyAudio(defaultBedrockReply);
          navigate(SCREENS.TELEPORT);
        }
      } else {
        addMessage({ text: data.text, type: MessageType.USER });
      }
    }
  }

  const generatePollyAudio = async (
    text: string,
    engine?: string,
    voiceId?: string,
  ) => {
    console.warn('generatePollyAudio muted', isMutedGlob);
    if (isMutedGlob) {
      return;
    }

    if (isAudioPlaybackActiveRef.current && visualizerInstance) {
      visualizerInstance.stopAudio();
    }
    isAudioPlaybackActiveRef.current = false;

    const pollyResponse = await AssistantQueryService.generateAssistantAudio({
      text,
      disableOptimizations: true,
      engine,
      voiceId,
    });

    console.warn('polly response', pollyResponse);

    if (pollyResponse.audio) {
      const arrayBuffer = AudioVisualizer.toArrayBuffer(pollyResponse.audio);
      onGenerateAudio(arrayBuffer);
    } else {
      console.warn('Error: missing Polly audio');
    }
  };

  const playBlockAudio = async (
    text: string,
    engine?: string,
    voiceId?: string,
  ) => {
    if (!open) {
      setOpen(true);
    }

    console.warn('playBlockAudio');
    const pollyResponse = await AssistantQueryService.generateAssistantAudio({
      text,
      disableOptimizations: true,
      engine,
      voiceId,
    });

    console.warn('polly response', pollyResponse);

    generatePollyAudio(text, engine, voiceId);
  };

  const cbOnCompletedStep = async (
    trigger: string,
    stepNumber: number,
    step: string,
  ) => {
    console.warn(`step ${step} completed`, {
      progressState: progressState.current,
      trigger,
    });

    const currentProgressState: { [key: string]: [number, number] } =
      JSON.parse(progressState.current || '{}');

    if (!currentProgressState[trigger]) {
      currentProgressState[trigger] = [0, Date.now()];
    }
    currentProgressState[trigger][0] = stepNumber;

    if (mytaverseEventProvider?.currentEventId || debugEventId) {
      await EventsService.setEventUserData(
        mytaverseEventProvider?.currentEventId ?? debugEventId,
        {
          assistantState: JSON.stringify(currentProgressState),
        },
      );
    }

    progressState.current = JSON.stringify(currentProgressState);
    console.warn(
      'assistant progress saved',
      JSON.stringify(currentProgressState, null, 2),
    );
  };

  const startFlow = (scriptTrigger: string, scriptId?: string) => {
    const script = scriptId
      ? scripts.find((x) => x.scriptId === scriptId)
      : scripts.find((x) => x.scriptTrigger === scriptTrigger);
    const scriptData = script?.scriptData;
    console.warn(JSON.stringify(scriptData, null, 2));

    let parsedScriptData: ScriptNodeJSON[] = [];

    if (scriptData) {
      try {
        parsedScriptData = JSON.parse(scriptData);
      } catch (error) {
        console.warn('makePlayerFromScriptData error', error);
      }
    }

    const localCurrentRegion = getCurrentRegion();

    const nodes: ScriptNode[] = parsedScriptData.map((node, index) => {
      // inject user name
      node.text = node.text.replace(
        '{{UserName}}',
        userName ? ', ' + userName : '',
      );

      return {
        id: `step-${index + 1}`,
        play: () => playBlockAudio(node.text, script?.engine, script?.voiceId),
        awaitButtonNext: node.awaitButtonNext ?? false,
        advanceOnAudioEnd: node.advanceOnAudioEnd ?? false,
        delayBeforeAdvancingMs: node.delayBeforeAdvancingMs ?? 0,
        delayAfterInputMs: node.delayAfterInputMs ?? 0,
        enableClickAnywhere: node.enableClickAnywhere ?? false,
        transitions: node.transitions || {},
        allowedKeys: node.allowedKeys || [],
        cbOnCompletedStep: () =>
          cbOnCompletedStep(localCurrentRegion, index + 1, `step-${index + 1}`),
      };
    });

    nodes.push({
      id: `step-${nodes.length + 1}`,
      play: async () => {
        linearScriptPlayerRef.current?.complete();
        cbOnCompletedStep(
          localCurrentRegion,
          nodes.length + 1,
          `step-${nodes.length + 1}`,
        );

        console.warn('completedScripts', completedScripts);
      },
    });

    console.warn('build nodes', nodes);

    const currentProgressState = JSON.parse(progressState.current || '{}');

    console.warn('currentProgressState player', { currentProgressState });

    if (
      !currentProgressState[currentRegion] ||
      (currentProgressState?.[currentRegion] &&
        currentProgressState?.[currentRegion][0] < nodes.length)
    ) {
      linearScriptPlayerRef.current = new LinearScriptPlayer(nodes, {
        name: currentRegion,
      });
      let step = 1;

      if (currentProgressState?.[currentRegion]) {
        step = currentProgressState?.[currentRegion][0] + 1;
      }

      linearScriptPlayerRef.current.start(`step-${step}`);
    }
  };

  const fetchScripts = async () => {
    const scripts = await AssistantScriptsService.getAssistantScripts();

    console.warn('loaded assistant scripts', scripts);

    setScripts(scripts);
  };

  const fetchParticipantProgress = async () => {
    if (!mytaverseEventProvider?.currentEventId) {
      // debug only
      const allParticipantEvents =
        await ParticipantsService.getCurrentParticipantEvents();
      const eventParticipantData = allParticipantEvents.events.find(
        (event) => event.eventId === debugEventId,
      );

      if (eventParticipantData) {
        progressState.current = eventParticipantData?.assistantState as string;
      } else {
        console.warn('no debug state data');
      }
    } else {
      progressState.current = mytaverseEventProvider.currentEventParticipant
        ?.assistantState as string;
    }

    console.warn('loaded participant progress', progressState.current);
  };

  useEffect(() => {
    const socketEventHandler = (msg: any) => {
      log('SocketEventResponse', msg);

      switch (msg.type) {
        case SocketEventResponseType.bedrock:
          onQueryBedrock(msg.data.text);
          break;
        case SocketEventResponseType.polly:
          onGenerateAudio(msg.data.audio);
          break;
        case SocketEventResponseType.error:
          log('Server error:', msg.data.message);
          break;
        case SocketEventResponseType.transcription:
          onTranscription(msg.data);
          break;
      }
    };

    socket.on(SocketEventDirection.response, socketEventHandler);

    return () => {
      socket.off(SocketEventDirection.response, socketEventHandler);
    };
  }, []);

  /*useEffect(() => {
    if (isAudioZonePlayback) {
      log('start audio zone playback');
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [isAudioZonePlayback]);

  useEffect(() => {
    log('start greetings playback', isGreetingsPlayback);

    if (isGreetingsPlayback) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [isGreetingsPlayback]);*/

  if (!mytaverseEventProvider) {
    mytaverseEventProvider = {} as any;
  }

  useEffect(() => {
    console.warn('region has changed to', mytaverseEventProvider.currentRegion);

    if (mytaverseEventProvider.currentRegion?.region) {
      currentRegion = mytaverseEventProvider.currentRegion?.region;
    }

    const scriptData = scripts.find((x) => {
      const scriptTrigger = (
        Array.isArray(x.scriptTrigger) ? x.scriptTrigger : [x.scriptTrigger]
      ).map((trigger) =>
        trigger.includes('_') ? regionNameToCamelCase(trigger) : trigger,
      );

      if (
        scriptTrigger.includes(
          mytaverseEventProvider.currentRegion?.region as string,
        )
      ) {
        return true;
      }
    });

    if (scriptData) {
      startFlow('', scriptData.scriptId);
    }
  }, [mytaverseEventProvider.currentRegion]);

  useAsyncEffect(async () => {
    const userChatHistory = chatHistory.filter((x) => x.type === 'USER');

    if (
      userChatHistory.length &&
      awaitingResponseType === AWAITING_RESPONSE_TYPE.LONG
    ) {
      shared.audioPlayBackType = EAudioPlaybackType.CHAT;
      socket.emit(SocketEventDirection.request, {
        type: SocketEventRequestType.bedrock,
        data: {
          text: userChatHistory[userChatHistory.length - 1].text,
          isGenericModel: true,
        },
      });
    }
  }, [awaitingResponseType, chatHistory]);

  useAsyncEffect(async () => {
    await fetchScripts();

    console.warn('completedScripts', completedScripts);

    await fetchParticipantProgress();
  }, []);

  useEffect(() => {
    console.warn('current assistant progress state', progressState);
  }, [progressState]);

  return (
    <AssistantContext.Provider
      value={{
        activeScreen,
        setActiveScreen,
        open,
        setOpen,
        handleSendRequest,
        chatHistory,
        navigate,
        setNavigationHistory,
        awaitingResponseType,
        stopPlayback,
        isAIEnabledForEvent,
        isRecording,
        setIsRecording,
        audioPromptHistory,
        isMuted,
        setIsMuted,
        teleportToRegion,
        startFlow,
        linearScriptPlayerRef,
        isAudioPlaybackActiveRef,
        isAudioPlaybackActive,
        scripts,
        isDisplayUserFeedback,
        setIsDisplayUserFeedback,
        handleSubmitUserFeedback,
        fetchScripts,
        fetchParticipantProgress,
      }}
    >
      {children}
    </AssistantContext.Provider>
  );
};
